import { pendingTask, begin, end } from '../constants/pendingTask';
import * as actions from './actionTypes';
import { fetchFromReitApi as fetch } from './fetchFromApi';
import * as actionHelpers from '../scripts/actionHelpers';
import { download } from '../scripts/fileHelpers';

export const fetchTenantServiceData = (
  clientId,
  quarterDescription,
  periodId
) => {
  return (dispatch) => {
    let query = `clientId=${clientId}`;
    if (quarterDescription !== null && quarterDescription !== undefined) {
      query += `&quarterDescription=${encodeURIComponent(quarterDescription)}`;
    }
    if (periodId !== null && periodId !== undefined) {
      query += `&reportPeriodId=${encodeURIComponent(periodId)}`;
    }

    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });
    return fetch(`/tenantServices?${query}`)
      .then((response) => {
        return response.json();
      })
      .then((tenantServicesData) => {
        if (actionHelpers.isErrorResponse(tenantServicesData)) {
          return actionHelpers.dispatchErrorAndEndTask(
            dispatch,
            actions.LOAD_TENANT_SERVICES_FAILURE,
            null
          );
        }
        dispatch({
          type: actions.LOAD_TENANT_SERVICES_SUCCESS,
          tenantServicesData,
          [pendingTask]: end,
        });
        return tenantServicesData;
      })
      .catch((error) => {
        actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.LOAD_TENANT_SERVICES_FAILURE,
          null,
          error
        );
      });
  };
};

export function exportTenantServicesData(
  clientId,
  reportPeriod,
  reportPeriodId
) {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    let url = `/tenantServices/exportTenantServices?clientId=${clientId}`;
    if (reportPeriod !== null && reportPeriod !== undefined) {
      url += `&reportPeriod=${encodeURIComponent(reportPeriod)}`;
    }
    if (reportPeriodId !== null && reportPeriodId !== undefined) {
      url += `&reportPeriodId=${encodeURIComponent(reportPeriodId)}`;
    }

    return fetch(url, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Unexpected error');
        }

        return response.blob().then(function (blob) {
          const contentDisposition = response.headers.get(
            'Content-Disposition'
          );
          const filename = contentDisposition
            .split(';')[1]
            .split('=')[1]
            .trim();
          download(blob, filename);
          dispatch({ type: actions.TASK_END, [pendingTask]: end });
        });
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.TASK_END,
          null,
          error
        );
      });
  };
}

export function uploadTenantServicesTemplate(
  clientId,
  file,
  reportPeriod,
  reportPeriodId
) {
  let formData = new FormData();
  formData.append('clientID', clientId);
  formData.append('file', file);
  formData.append('reportPeriod', reportPeriod);
  if (reportPeriodId !== null && reportPeriodId !== undefined) {
    formData.append('reportPeriodId', reportPeriodId);
  }
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    return fetch('/tenantServices/importTenantServices', {
      method: 'POST',
      body: formData,
    })
      .then((response) => {
        return response.json();
      })
      .then((response) => {
        if (actionHelpers.isErrorResponse(response)) {
          return actionHelpers.dispatchErrorAndEndTask(
            dispatch,
            actions.UPLOAD_Tenant_SERVICES_FAILURE,
            response
          );
        }
        dispatch({ type: actions.TASK_END, [pendingTask]: end });
        return response;
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.UPLOAD_Tenant_SERVICES_FAILURE,
          null,
          error
        );
      });
  };
}

export function exportTenantServicesErrorFile(clientId, fileUploadId) {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    return fetch(
      `/tenantServices/downloadTenantServicesErrorReport?clientId=${clientId}&fileUploadId=${fileUploadId}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Unexpected error');
        }

        return response.blob().then(function (blob) {
          const contentDisposition = response.headers.get(
            'Content-Disposition'
          );
          const filename = contentDisposition
            .split(';')[1]
            .split('=')[1]
            .trim();
          download(blob, filename);
          dispatch({ type: actions.TASK_END, [pendingTask]: end });
        });
      })
      .catch((error) => {
        return actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.TASK_END,
          null,
          error
        );
      });
  };
}

/**
 * Manages tenant services, including creation, update, and deletion.
 *
 * Sends a request to `/tenantServices/manageTenantServices`, performing the required action
 * based on the provided request data. If the operation fails, it dispatches the
 * `MANAGE_TENANT_SERVICES_FAILURE` action.
 *
 * @param {Object} request - The request object containing tenant service details.
 * @returns {Promise} A Promise resolving with the API response, or dispatching an error action if failed.
 */
export function manageTenantServices(request) {
  return function (dispatch) {
    dispatch({ type: actions.TASK_BEGIN, [pendingTask]: begin });

    return fetch(`/tenantServices/manageTenantServices`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify(request),
    })
      .then((response) => {
        return response.json();
      })
      .then((result) => {
        if (actionHelpers.isErrorResponse(result)) {
          return actionHelpers.dispatchErrorAndEndTask(
            dispatch,
            actions.MANAGE_TENANT_SERVICES_FAILURE,
            result
          );
        }

        dispatch({
          type: actions.MANAGE_TENANT_SERVICES_SUCCESS,
          result,
          [pendingTask]: end,
        });
      })
      .catch((error) => {
        actionHelpers.dispatchErrorAndEndTask(
          dispatch,
          actions.MANAGE_TENANT_SERVICES_FAILURE,
          null,
          error
        );
      });
  };
}
